<script setup lang="ts">
import { computed, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { PfmeaBatchPreview, PfmeaBatchSheet } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/**
 * 整簿批量导入：一次把工作簿里的 N 张表建成 N 条 PFMEA。
 *
 * 东禾的 PFMEA 是「一个零件一张表」——ONVO Qingling 那份 14 张、
 * 小鹏 D11 那份 12 张。单表导入要点十几轮，没人愿意干第二次。
 *
 * 放在 PFMEA 页而不是结构树里：项目一条 PFMEA 都没有时也要能用，
 * 而结构树必须先有一条记录才存在。
 */
const props = defineProps<{ projectId: string | number }>();
const emit = defineEmits<{ imported: [] }>();

const open = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const preview = ref<PfmeaBatchPreview | null>(null);
const loading = ref(false);
const confirming = ref(false);
/** 勾选要导入的表；键是工作表名 */
const picked = ref<Set<string>>(new Set());
/** 同名记录里勾了「覆盖」的表 */
const replacing = ref<Set<string>>(new Set());

function reset(): void {
  preview.value = null;
  picked.value = new Set();
  replacing.value = new Set();
  if (fileInput.value) fileInput.value.value = '';
}

function toggle(): void {
  open.value = !open.value;
  reset();
}

function importable(sheet: PfmeaBatchSheet): boolean {
  return sheet.looksLikePfmea && !sheet.errors.length && sheet.sourceRows > 0;
}

async function runPreview(): Promise<void> {
  const file = fileInput.value?.files?.[0];
  if (!file) {
    notify('error', '请选择 PFMEA 工作簿', '支持 .xlsx，单个文件不超过 20MB。');
    return;
  }
  loading.value = true;
  try {
    const form = new FormData();
    form.append('file', file);
    const result = await eqsApi.batchPreviewPfmeaImport(props.projectId, form);
    preview.value = result;
    // 默认勾上所有能导的；有同名记录的不默认覆盖，让人自己决定
    picked.value = new Set(result.sheets.filter(importable).map((sheet) => sheet.sheet));
    replacing.value = new Set();
    if (!result.importableSheets) {
      notify('error', '没有可导入的工作表', '这个工作簿里没有识别出 PFMEA 表；请检查表头是否包含过程步骤、功能、失效模式。');
    }
  } catch (caught) {
    preview.value = null;
    notify('error', '解析失败', errorMessage(caught));
  } finally {
    loading.value = false;
  }
}

function togglePick(sheet: PfmeaBatchSheet): void {
  const next = new Set(picked.value);
  if (next.has(sheet.sheet)) next.delete(sheet.sheet);
  else next.add(sheet.sheet);
  picked.value = next;
}

function toggleReplace(sheet: PfmeaBatchSheet): void {
  const next = new Set(replacing.value);
  if (next.has(sheet.sheet)) next.delete(sheet.sheet);
  else next.add(sheet.sheet);
  replacing.value = next;
}

const selected = computed(() =>
  (preview.value?.sheets ?? []).filter((sheet) => importable(sheet) && picked.value.has(sheet.sheet)));

/** 勾了导入、但同名记录已有内容又没勾覆盖的表：会被后端拒，先在界面上拦住 */
const blocked = computed(() => selected.value.filter((sheet) =>
  sheet.existingDocumentId !== null && sheet.existingItemCount > 0 && !replacing.value.has(sheet.sheet)));

const totalRows = computed(() => selected.value.reduce((sum, sheet) => sum + sheet.sourceRows, 0));

/* ---------- 按零件名称关联 BOM ---------- */

interface LinkPlan {
  bomParts: number;
  documents: number;
  matched: number;
  unmatched: number;
  ambiguous: number;
  alreadyLinked: number;
  matches: Array<{ title: string; matchedPartNumber: string | null; kind: string; linkedPartNumber: string | null }>;
}

const linkPlan = ref<LinkPlan | null>(null);
const linking = ref(false);

async function loadLinkPlan(): Promise<void> {
  linking.value = true;
  try {
    linkPlan.value = await eqsApi.planPfmeaBomLink(props.projectId) as unknown as LinkPlan;
  } catch (caught) {
    linkPlan.value = null;
    notify('error', '读取关联情况失败', errorMessage(caught));
  } finally {
    linking.value = false;
  }
}

async function applyLink(): Promise<void> {
  linking.value = true;
  try {
    const result = await eqsApi.applyPfmeaBomLink(props.projectId);
    if (result.linked) {
      notify('success', `已关联 ${result.linked} 条 PFMEA`, '这些 PFMEA 现在能对应到 BOM 里的具体零件。');
    } else {
      notify('info', '没有需要新建的关联', result.note ?? '');
    }
    await loadLinkPlan();
    emit('imported');
  } catch (caught) {
    notify('error', '关联失败', errorMessage(caught));
  } finally {
    linking.value = false;
  }
}

async function runConfirm(): Promise<void> {
  if (!selected.value.length) return;
  const replaceCount = selected.value.filter((sheet) => replacing.value.has(sheet.sheet)).length;
  const confirmed = await confirmAction({
    title: `确认导入 ${selected.value.length} 张工作表？`,
    message: replaceCount
      ? `其中 ${replaceCount} 条会覆盖已有结构，原有条目将被删除且不可恢复。`
      : `将新建 ${selected.value.length} 条 PFMEA 记录，共 ${totalRows.value} 行来源数据。`,
    danger: replaceCount > 0,
    confirmLabel: '开始导入',
  });
  if (!confirmed) return;

  confirming.value = true;
  try {
    const result = await eqsApi.batchConfirmPfmeaImport(
      props.projectId,
      selected.value.map((sheet) => ({
        sheet: sheet.sheet,
        records: sheet.records,
        targetDocumentId: sheet.existingDocumentId,
        replaceExisting: replacing.value.has(sheet.sheet),
      })),
      preview.value?.fileName,
    );
    notify('success', `已导入 ${result.documents} 条 PFMEA`, `共写入 ${result.imported} 个结构化条目。`);
    open.value = false;
    reset();
    emit('imported');
  } catch (caught) {
    // 后端是整批事务：失败即全部回滚，这里如实说明没有半份数据
    notify('error', '导入失败，未写入任何数据', errorMessage(caught));
  } finally {
    confirming.value = false;
  }
}
</script>

<template>
  <section class="pfmea-batch">
    <!--
      0910 Tony:「很多页面都凑一块了」。这个头原来带两行说明、常驻 120px 高，
      压在贯通核对和记录列表之间。说明搬进展开区——**没点开的时候它只是个入口，
      不需要在那儿解释怎么用**；点开了才需要。
    -->
    <header>
      <div>
        <span>BATCH IMPORT</span>
        <b>整簿导入 PFMEA</b>
      </div>
      <button class="inline-button" type="button" :disabled="loading || confirming" @click="toggle">
        {{ open ? '收起' : '整簿导入' }}
      </button>
    </header>

    <template v-if="open">
      <p class="pfmea-batch-intro">
        一个工作簿里有几张表就建几条 PFMEA（一个零件一张表的写法）。上传后先预览，确认前不写入。
      </p>
      <!-- 关联 BOM：导完之后的下一步，放在同一个面板里免得人找不到 -->
      <div class="pfmea-link">
        <div>
          <b>按零件名称关联 BOM</b>
          <p v-if="!linkPlan">把每条 PFMEA 对应到 BOM 里的顶层零件，之后才能回答「这个零件的失效分析做了没有」。</p>
          <p v-else>
            BOM 顶层零件 {{ linkPlan.bomParts }} 个，PFMEA {{ linkPlan.documents }} 条：
            名称能对上 <b>{{ linkPlan.matched }}</b> 条<template v-if="linkPlan.alreadyLinked">（已挂 {{ linkPlan.alreadyLinked }} 条）</template><template
              v-if="linkPlan.unmatched">，对不上 <b class="warn">{{ linkPlan.unmatched }}</b> 条</template><template
              v-if="linkPlan.ambiguous">，重名 <b class="warn">{{ linkPlan.ambiguous }}</b> 条</template>。
          </p>
        </div>
        <div class="pfmea-link-actions">
          <button class="inline-button" type="button" :disabled="linking" @click="loadLinkPlan">
            {{ linking ? '处理中…' : '查看匹配情况' }}
          </button>
          <button
            v-if="linkPlan && linkPlan.matched > linkPlan.alreadyLinked"
            class="button button-secondary" type="button" :disabled="linking" @click="applyLink"
          >关联 {{ linkPlan.matched - linkPlan.alreadyLinked }} 条</button>
        </div>
      </div>

      <div class="pfmea-batch-file">
        <input ref="fileInput" type="file" accept=".xlsx" @change="reset">
        <button class="button button-secondary" type="button" :disabled="loading || confirming" @click="runPreview">
          {{ loading ? '正在识别…' : '解析工作簿' }}
        </button>
      </div>

      <template v-if="preview">
        <p class="pfmea-batch-summary">
          {{ preview.fileName }}：共 {{ preview.totalSheets }} 张表，
          可导入 <b>{{ preview.importableSheets }}</b> 张<template v-if="preview.conflictSheets">，
          其中 <b class="warn">{{ preview.conflictSheets }}</b> 张与已有 PFMEA 同名</template><template
            v-if="preview.skippedSheets">，{{ preview.skippedSheets }} 张不是 PFMEA 表</template>。
        </p>

        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>导入</th>
                <th>工作表</th>
                <th>解析结果</th>
                <th>已有同名记录</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sheet in preview.sheets" :key="sheet.sheet" :class="{ off: !importable(sheet) }">
                <td>
                  <input
                    v-if="importable(sheet)"
                    type="checkbox"
                    :checked="picked.has(sheet.sheet)"
                    :aria-label="`导入 ${sheet.sheet}`"
                    @change="togglePick(sheet)"
                  >
                  <span v-else class="skip">跳过</span>
                </td>
                <td><b>{{ sheet.sheet }}</b></td>
                <td>
                  <template v-if="importable(sheet)">
                    {{ sheet.sourceRows }} 行 →
                    步骤 {{ sheet.summary?.processSteps ?? 0 }} /
                    功能 {{ sheet.summary?.functions ?? 0 }} /
                    失效模式 {{ sheet.summary?.failureModes ?? 0 }} /
                    措施 {{ sheet.summary?.actions ?? 0 }}
                    <small v-if="sheet.warnings.length">{{ sheet.warnings.length }} 条提示</small>
                  </template>
                  <template v-else-if="sheet.errors.length">
                    <span class="bad">{{ sheet.errors.length }} 处错误：第 {{ sheet.errors[0].row }} 行 {{ sheet.errors[0].message }}</span>
                  </template>
                  <span v-else class="muted">{{ sheet.error || '不是 PFMEA 表' }}</span>
                </td>
                <td>
                  <template v-if="sheet.existingDocumentId !== null">
                    <span class="warn">已有 {{ sheet.existingItemCount }} 个条目</span>
                    <label v-if="importable(sheet) && sheet.existingItemCount > 0" class="replace-pick">
                      <input
                        type="checkbox"
                        :checked="replacing.has(sheet.sheet)"
                        @change="toggleReplace(sheet)"
                      >
                      <span>覆盖</span>
                    </label>
                  </template>
                  <span v-else class="muted">新建</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="blocked.length" class="pfmea-batch-blocked">
          有 {{ blocked.length }} 张表与已有 PFMEA 同名且对方已有内容：勾选「覆盖」才能导入，或取消勾选这几张表。
        </p>

        <div class="pfmea-batch-actions">
          <button
            class="button button-primary"
            type="button"
            :disabled="confirming || !selected.length || blocked.length > 0"
            @click="runConfirm"
          >
            {{ confirming ? '正在导入…' : `确认导入 ${selected.length} 张表（${totalRows} 行）` }}
          </button>
          <small>导入是一个事务：中途失败会整体回滚，不会留下半份工作簿。</small>
        </div>
      </template>
    </template>
  </section>
</template>

<style scoped>
.pfmea-batch-intro { margin: 0 0 8px; color: var(--muted); font-size: 12px; line-height: 1.6; }
.pfmea-batch { margin-bottom: 18px; border: 1px solid var(--line); background: var(--white); }
.pfmea-batch > header {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
  padding: 14px 16px;
}
.pfmea-batch > header span { color: var(--blue); font: 800 10px var(--display); letter-spacing: .12em; }
.pfmea-batch > header b { display: block; margin: 3px 0 4px; font-size: 15px; }
.pfmea-batch > header p { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.pfmea-link {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 14px;
  margin: 0 16px 12px; padding: 12px 14px; border: 1px solid var(--line); background: var(--paper);
}
.pfmea-link b { font-size: 13px; }
.pfmea-link p { margin: 4px 0 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.pfmea-link-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.pfmea-batch-file { display: flex; align-items: center; gap: 10px; padding: 0 16px 12px; flex-wrap: wrap; }
.pfmea-batch-summary { margin: 0; padding: 0 16px 10px; color: var(--ink); font-size: 13px; line-height: 1.8; }
.pfmea-batch .data-table-wrap { max-height: 420px; overflow: auto; border-top: 1px solid var(--line); }
.pfmea-batch td { vertical-align: top; font-size: 12px; }
.pfmea-batch tr.off { color: var(--muted); background: var(--paper); }
.pfmea-batch .skip { color: var(--muted); font-size: 12px; }
.pfmea-batch .muted { color: var(--muted); }
.pfmea-batch .warn { color: var(--warning); font-weight: 700; }
.pfmea-batch .bad { color: var(--risk); }
.pfmea-batch td small { display: block; margin-top: 3px; color: var(--muted); }
.replace-pick { display: inline-flex; align-items: center; gap: 4px; margin-left: 8px; cursor: pointer; }
.pfmea-batch-blocked { margin: 0; padding: 10px 16px; color: var(--risk); font-size: 12px; line-height: 1.7; }
.pfmea-batch-actions {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-top: 1px solid var(--line);
}
.pfmea-batch-actions small { color: var(--muted); font-size: 12px; }
</style>
